import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "antd-mobile";
import UserBottomNav from "@/components/feature/UserBottomNav";
import { useUserStore } from "@/store/useUserStore";
import { fetchUserProfile, verifyIdentity, type UserProfile } from "@/api/user";
import { fetchAiOrderList } from "@/api/aiOrder";
import { http } from "@/http/axios";
import { toastSuccess, toastError } from "@/utils/toast";
import { useVoiceStore } from "@/store/useVoiceStore";

const BRAND = "#FF6B35";

type QuickAction = {
  icon: string;
  label: string;
  badge?: string;
  tip?: string | null;
  color: string;
  bg: string;
  path: string;
};

const actions: QuickAction[] = [
  {
    icon: "ri-wallet-3-line",
    label: "我的钱包",
    tip: "",
    color: "#10B981",
    bg: "#EDFCF5",
    path: "/wallet",
  },
  {
    icon: "ri-gift-2-line",
    label: "邀请好友",
    tip: null,
    color: "#8B5CF6",
    bg: "#F3F0FF",
    path: "/invite",
  },
  {
    icon: "ri-customer-service-2-line",
    label: "在线客服",
    tip: null,
    color: "#FF6B35",
    bg: "#FFF3EE",
    path: "/ai-voice",
  },
];

/** ★ 仅达人可见的快捷入口（追加在「在线客服」之后） */
const TALENT_ACTION: QuickAction = {
  icon: "ri-briefcase-4-line",
  label: "工作台",
  tip: null,
  color: "#2563EB",
  bg: "#EFF6FF",
  path: "/workspace",
};

const groups = [
  {
    title: "旅行服务",
    items: [
      {
        icon: "ri-file-list-3-line",
        label: "全部订单",
        sub: "查看所有旅行订单",
        color: BRAND,
        path: "/orders",
      },
      {
        icon: "ri-chat-quote-line",
        label: "需求询单",
        sub: "AI套餐询单·管家跟进",
        color: "#7C3AED",
        path: "/inquiry-orders",
      },
      // TODO: 后期会用到 - 电子合同
      // {
      //   icon: "ri-file-paper-2-line",
      //   label: "电子合同",
      //   sub: "查看已签署合同",
      //   color: "#6366F1",
      //   path: "/contracts",
      // },
    ],
  },
  {
    title: "财务",
    items: [
      {
        icon: "ri-receipt-line",
        label: "电子发票",
        sub: "申请和查看发票",
        color: "#F59E0B",
        path: "/invoices",
      },
    ],
  },
  {
    title: "帮助",
    items: [
      {
        icon: "ri-settings-3-line",
        label: "设置",
        sub: "账号与隐私设置",
        color: "#6B7280",
        path: "/settings",
      },
    ],
  },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const [showVerify, setShowVerify] = useState(false);

  // 用户详情数据（从API获取）
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  // ★ 本页接口返回的达人身份（唯一可信来源，避免 localStorage 残留导致误显示工作台）
  const [profileIsTalent, setProfileIsTalent] = useState<number | null>(null);
  const [inquiryCount, setInquiryCount] = useState(0);

  // 实名认证表单
  const [realNameInput, setRealNameInput] = useState("");
  const [cardIdInput, setCardIdInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    realName?: string;
    cardId?: string;
    phone?: string;
  }>({});

  // 从 Zustand 获取用户信息（兜底）
  const userInfo = useUserStore((state) => state.userInfo);
  const wechatUser = useUserStore((state) => state.wechatUser);
  const storeIsTalent = useUserStore((state) => state.isTalent);
  const token = useUserStore((state) => state.token);
  const isTokenExpired = useUserStore((state) => state.isTokenExpired);
  const logout = useUserStore((state) => state.logout);

  // 加载用户详细信息
  useEffect(() => {
    if (!token || isTokenExpired()) {
      setLoadingProfile(false);
      return;
    }

    fetchUserProfile()
      .then((data) => {
        setProfile(data.user);
        // ★ 只信任接口返回值：非 1 一律按 0 处理，绝不因缓存残留误显示工作台
        const talentValue = (data.is_talent ?? data.user.is_talent) === 1 ? 1 : 0;
        setProfileIsTalent(talentValue);
        // 同步达人身份到 Zustand store + 独立 localStorage 缓存（供其他页面冷启动兜底）
        useUserStore.getState().setIsTalent(talentValue);
        localStorage.setItem("is_talent_cache", String(talentValue));
        if (data.user && useUserStore.getState().userInfo) {
          useUserStore.getState().updateUserInfo({
            nickname: data.user.nickname || userInfo?.nickname,
            avatar: data.user.avatar || userInfo?.avatar,
            phone: data.user.phone || userInfo?.phone,
            real_name: data.user.real_name || userInfo?.real_name,
          });
        }
      })
      .catch((err) => {
        console.error("获取用户信息失败:", err);
        // ★ 接口失败 → 显式置 0（非达人），避免上个账号残留 isTalent=1 误显示工作台
        setProfileIsTalent(0);
        useUserStore.getState().setIsTalent(0);
        localStorage.setItem("is_talent_cache", "0");
      })
      .finally(() => setLoadingProfile(false));
  }, [
    userInfo?.avatar,
    userInfo?.nickname,
    userInfo?.phone,
    userInfo?.real_name,
    token,
    isTokenExpired,
  ]);

  // 加载询单数量
  // ★ 不依赖 isTokenExpired() 前置检查（Zustand persist 异步恢复时序可能导致误判），
  //   直接发请求，由 axios 拦截器统一处理认证问题；用 _skipAuthRedirect 避免 401 跳转登录页
  useEffect(() => {
    // 延迟 500ms 确保 Zustand persist 从 localStorage 恢复完成（生产环境异步延迟更大）
    const timer = setTimeout(() => {
      http.get("/home/ai-order/list", {
        params: { page: 1, limit: 1 },
        _skipAuthRedirect: true,
      } as any)
        .then((res) => setInquiryCount(res.data?.data?.count ?? 0))
        .catch(() => setInquiryCount(0));
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // ★ 是否有可用缓存（避免闪烁：有缓存时直接展示，无缓存时显示骨架屏等待 API）
  const hasCachedData = !!(userInfo || wechatUser || profile);

  // 优先使用API数据，否则使用Zustand兜底
  const nickname =
    profile?.nickname ||
    wechatUser?.nickname ||
    userInfo?.nickname ||
    "微信用户";
  const avatar =
    profile?.avatar ||
    wechatUser?.avatar ||
    userInfo?.avatar ||
    "https://readdy.ai/api/search-image?query=young%20cheerful%20asian%20woman%20natural%20portrait%20smile%20warm%20background%20clean%20minimal%20soft%20light&width=68&height=68&seq=pf_av3&orientation=squarish";
  const phone = profile?.phone || userInfo?.phone || "138****8888";
  const isRealVerified = profile?.is_real === 1;

  // ★ 达人身份判断：只信任「本页接口返回值」与「store 中的 1」，不使用 localStorage 缓存兜底
  //   —— 从根源杜绝别的账号残留缓存导致非达人误显示「工作台」
  //   两者只要有一个明确为 1 就显示；都未知/非 1 → 一律不显示（安全方向）
  const isTalent =
    profileIsTalent === 1 || storeIsTalent === 1 ? 1 : 0;
  // 达人 → 在「在线客服」后追加「工作台」入口；非达人 → 不显示
  const quickActions =
    isTalent === 1 ? [...actions, TALENT_ACTION] : actions;

  // 正则验证
  const PHONE_REGEX = /^1[3-9]\d{9}$/;
  const CARD_ID_REGEX = /^\d{17}[\dXx]$/;

  // 关闭弹窗并清空表单
  const handleCloseModal = useCallback(() => {
    setShowVerify(false);
    setRealNameInput("");
    setCardIdInput("");
    setPhoneInput("");
    setErrors({});
  }, []);

  // 提交实名认证
  const handleSubmitVerify = async () => {
    const newErrors: { realName?: string; cardId?: string; phone?: string } =
      {};

    if (!realNameInput.trim()) {
      newErrors.realName = "姓名输入有误，请重新输入";
    }
    if (!cardIdInput.trim()) {
      newErrors.cardId = "身份证号输入有误，请重新输入";
    } else if (!CARD_ID_REGEX.test(cardIdInput)) {
      newErrors.cardId = "身份证号格式有误，请重新输入";
    }
    if (!phoneInput.trim()) {
      newErrors.phone = "手机号输入有误，请重新输入";
    } else if (!PHONE_REGEX.test(phoneInput)) {
      newErrors.phone = "手机号格式有误，请重新输入";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    try {
      setSubmitting(true);
      await verifyIdentity({
        real_name: realNameInput,
        card_id: cardIdInput,
        phone: phoneInput,
      });
      // 刷新用户信息
      const res = await fetchUserProfile();
      setProfile(res.user);
      // ★ 只信任接口返回值（非 1 按 0 处理）
      const talentValue = (res.is_talent ?? res.user.is_talent) === 1 ? 1 : 0;
      setProfileIsTalent(talentValue);
      // 同步达人身份到 Zustand store + 独立 localStorage 缓存
      useUserStore.getState().setIsTalent(talentValue);
      localStorage.setItem("is_talent_cache", String(talentValue));
      toastSuccess("实名认证成功");
      // 延迟关闭弹窗，让用户看到成功提示
      setTimeout(() => {
        handleCloseModal();
      }, 1200);
    } catch (err: any) {
      console.error("实名认证失败:", err);
      const errMsg = err.message || "认证失败，请稍后重试";
      toastError(errMsg);
      // 根据错误信息匹配字段，显示对应红框提示
      const apiErrors: { realName?: string; cardId?: string; phone?: string } =
        {};
      if (errMsg.includes("身份证")) {
        apiErrors.cardId = "身份证号输入有误，请重新输入";
      } else if (errMsg.includes("手机号")) {
        apiErrors.phone = "手机号输入有误，请重新输入";
      } else if (errMsg.includes("姓名")) {
        apiErrors.realName = "姓名输入有误，请重新输入";
      }
      if (Object.keys(apiErrors).length > 0) setErrors(apiErrors);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    Dialog.show({
      content: "确认退出登录吗？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "退出登录",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: () => {
              // 清空个人信息
              logout();
              // 清空会话
              useVoiceStore.getState().resetConversation();
              toastSuccess("已退出登录");
              window.setTimeout(() => {
                navigate("/ai-voice", { replace: true });
              }, 0);
            },
          },
        ],
      ],
    });
  };

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+96px)]"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* ── Profile header ── */}
      <div
        className="bg-white px-5 pb-5"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
      >
        {loadingProfile && !hasCachedData ? (
          /* ★ 骨架屏：无缓存时避免闪烁默认值 */
          <div className="flex items-center gap-4 animate-pulse">
            <div
              className="w-[68px] h-[68px] rounded-full bg-gray-200 flex-shrink-0"
              style={{ border: `3px solid #E5E7EB` }}
            />
            <div className="flex-1 min-w-0 space-y-2.5">
              <div className="h-6 w-28 rounded bg-gray-200" />
              <div className="h-4 w-36 rounded bg-gray-200" />
            </div>
          </div>
        ) : (
          /* ★ 有数据（缓存或 API 已返回）→ 正常渲染 */
          <>
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div
              className="w-[68px] h-[68px] rounded-full overflow-hidden"
              style={{ border: `3px solid ${BRAND}` }}
            >
              <img
                src={avatar}
                alt="avatar"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div
              className="absolute bottom-0 right-0 w-5 h-5 rounded-full flex items-center justify-center"
              style={{ background: "#10B981", border: "2px solid #fff" }}
            >
              <i
                className="ri-check-line text-white"
                style={{ fontSize: 10, fontWeight: 900 }}
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl font-bold text-gray-900">
                {nickname}
              </span>
              {loadingProfile ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 animate-pulse">
                  加载中
                </span>
              ) : isRealVerified ? (
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "#EDFCF5", color: "#10B981" }}
                >
                  已实名
                </span>
              ) : (
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "#FFF3EE", color: BRAND }}
                >
                  未实名
                </span>
              )}
            </div>
            <div className="text-sm text-gray-400 mb-3">{phone}</div>
            <div className="flex gap-2">
              {/* <button
                className="text-xs font-semibold px-4 py-1.5 rounded-full cursor-pointer active:scale-95 transition-all whitespace-nowrap"
                style={{ background: "#F5F5F5", color: "#555" }}
              >编辑资料</button> */}
              <button
                onClick={() => { if (!isRealVerified) setShowVerify(true); }}
                className="text-xs font-semibold px-4 py-1.5 rounded-full cursor-pointer active:scale-95 transition-all whitespace-nowrap"
                style={{
                  background: isRealVerified ? "#FFF3EE" : "#F5F5F5",
                  color: isRealVerified ? BRAND : "#999",
                }}
              >
                <i className={`mr-1 ${isRealVerified ? "ri-shield-check-line" : "ri-id-card-line"}`} style={{ fontSize: 11 }} />
                实名认证
              </button>
            </div>
          </div>
        </div>
          </>
        )}

        {/* Stats */}
      </div>

      <div className="px-4 pt-3 space-y-3">
        {/* ── Quick actions（达人在「在线客服」后追加「工作台」，共 4 项） ── */}
        <div
          className="grid gap-2.5"
          style={{ gridTemplateColumns: `repeat(${quickActions.length}, minmax(0, 1fr))` }}
        >
          {quickActions.map((a) => (
            <button
              key={a.label}
              onClick={() => navigate(a.path)}
              className="flex flex-col items-center gap-2 pt-4 pb-3.5 rounded-2xl cursor-pointer active:scale-95 transition-all relative"
              style={{ background: a.bg }}
            >
              {a.badge && (
                <span
                  className="absolute top-2 right-2 min-w-[16px] h-4 flex items-center justify-center rounded-full text-white px-1"
                  style={{ background: a.color, fontSize: 9, fontWeight: 700 }}
                >
                  {a.badge}
                </span>
              )}
              {a.tip && (
                <span
                  className="absolute top-2 right-2 text-xs font-bold px-1.5 py-0.5 rounded-full"
                  style={{
                    background: `${a.color}20`,
                    color: a.color,
                    fontSize: 9,
                  }}
                >
                  {a.tip}
                </span>
              )}
              <div
                className="w-11 h-11 flex items-center justify-center rounded-2xl"
                style={{ background: "rgba(255,255,255,0.75)" }}
              >
                <i
                  className={a.icon}
                  style={{ color: a.color, fontSize: 22 }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-700 text-center leading-tight whitespace-nowrap">
                {a.label}
              </span>
            </button>
          ))}
        </div>

        {/* ── Upcoming trip ── */}
        {/* <div className="bg-white rounded-2xl overflow-hidden">
          <div
            className="flex items-center justify-between px-4 pt-4 pb-3"
            style={{ borderBottom: "1px solid #F5F5F5" }}
          >
            <span className="text-sm font-bold text-gray-900">即将出行</span>
            <button
              className="text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-0.5"
              style={{ color: BRAND }}
              onClick={() => navigate("/orders")}
            >
              全部{" "}
              <i className="ri-arrow-right-s-line" style={{ fontSize: 13 }} />
            </button>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Sanya%20beach%20resort%20pool%20tropical%20sunshine%20aerial%20view%20luxury%20hotel&width=56&height=56&seq=pf_trip3&orientation=squarish"
                  alt="trip"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-gray-900 mb-0.5">
                  三亚5天4晚度假套餐
                </div>
                <div className="text-xs text-gray-400 mb-2">
                  05-01 出发 · 4人
                </div>
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full"
                  style={{ background: "#FFF3EE", color: BRAND }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full inline-block"
                    style={{ background: BRAND }}
                  />
                  待出行
                </span>
              </div>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer flex-shrink-0"
                style={{ background: "#F5F5F5" }}
                onClick={() => navigate("/orders")}
              >
                <i
                  className="ri-arrow-right-s-line text-gray-400"
                  style={{ fontSize: 18 }}
                />
              </button>
            </div>
          </div>
        </div>
          </>
        )} */}

        {/* ── Service groups ── */}
        {groups.map((g) => (
          <div key={g.title} className="bg-white rounded-2xl overflow-hidden">
            <div className="px-4 pt-3 pb-1">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                {g.title}
              </span>
            </div>
            {g.items.map((item, idx) => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer active:bg-gray-50 transition-colors"
                style={{ borderTop: idx > 0 ? "1px solid #F5F5F5" : "none" }}
              >
                <div
                  className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: `${item.color}15` }}
                >
                  <i
                    className={item.icon}
                    style={{ color: item.color, fontSize: 18 }}
                  />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">
                      {item.label}
                    </span>
                    {((item as { badge?: string }).badge || item.label === "需求询单") && (item.label !== "需求询单" || inquiryCount > 0) && (
                      <span
                        className="text-xs font-bold px-1.5 py-0.5 rounded-full text-white"
                        style={{ background: item.color, fontSize: 10 }}
                      >
                        {item.label === "需求询单"
                          ? inquiryCount > 99 ? "99+" : String(inquiryCount)
                          : (item as { badge?: string }).badge}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">{item.sub}</div>
                </div>
                <i
                  className="ri-arrow-right-s-line text-gray-300 flex-shrink-0"
                  style={{ fontSize: 18 }}
                />
              </button>
            ))}
          </div>
        ))}

        {/* Logout */}
        <button
          className="w-full py-3.5 rounded-2xl text-sm font-semibold cursor-pointer active:scale-[0.98] transition-all"
          style={{ background: "#FFF1F2", color: "#F43F5E" }}
          onClick={handleLogout}
        >
          退出登录
        </button>

        <div className="h-2" />
      </div>

      {/* ── Real name modal ── */}
      {showVerify && (
        <div
          className="fixed inset-0 z-[999] flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}
        >
          <div
            className="bg-white rounded-t-3xl w-full relative flex flex-col"
            style={{ maxWidth: 480, maxHeight: "80vh" }}
          >
            {/* 可滚动内容区 */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* 右上角关闭按钮 */}
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer hover:bg-gray-100 transition-colors z-10"
              >
                <i
                  className="ri-close-line text-gray-400"
                  style={{ fontSize: 20 }}
                />
              </button>

              <div className="w-9 h-1 rounded-full bg-gray-200 mx-auto mb-5" />
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-2xl"
                  style={{ background: "#FFF3EE" }}
                >
                  <i
                    className="ri-id-card-line"
                    style={{ color: BRAND, fontSize: 26 }}
                  />
                </div>
                <div>
                  <div className="text-base font-bold text-gray-900">
                    实名认证
                  </div>
                  <div className="text-xs text-gray-400">
                    认证后可享受更多服务保障
                  </div>
                </div>
              </div>
              <div className="space-y-2.5 mb-4">
                {/* 姓名 */}
                <div>
                  <div
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors ${errors.realName ? "bg-red-50" : "bg-gray-50"}`}
                    style={
                      errors.realName
                        ? { border: "1px solid #F43F5E" }
                        : undefined
                    }
                  >
                    <i
                      className="ri-user-line"
                      style={{
                        color: errors.realName ? "#F43F5E" : "#C0C4CC",
                        fontSize: 16,
                      }}
                    />
                    <input
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                      placeholder="请输入真实姓名"
                      value={realNameInput}
                      onChange={(e) => {
                        setRealNameInput(e.target.value);
                        if (errors.realName)
                          setErrors((prev) => ({
                            ...prev,
                            realName: undefined,
                          }));
                      }}
                    />
                  </div>
                  {errors.realName && (
                    <p className="text-xs text-red-500 pl-4 mt-1">
                      {errors.realName}
                    </p>
                  )}
                </div>
                {/* 身份证 */}
                <div>
                  <div
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors ${errors.cardId ? "bg-red-50" : "bg-gray-50"}`}
                    style={
                      errors.cardId
                        ? { border: "1px solid #F43F5E" }
                        : undefined
                    }
                  >
                    <i
                      className="ri-bank-card-line"
                      style={{
                        color: errors.cardId ? "#F43F5E" : "#C0C4CC",
                        fontSize: 16,
                      }}
                    />
                    <input
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                      placeholder="请输入身份证号"
                      value={cardIdInput}
                      onChange={(e) => {
                        setCardIdInput(e.target.value);
                        if (errors.cardId)
                          setErrors((prev) => ({ ...prev, cardId: undefined }));
                      }}
                    />
                  </div>
                  {errors.cardId && (
                    <p className="text-xs text-red-500 pl-4 mt-1">
                      {errors.cardId}
                    </p>
                  )}
                </div>
                {/* 手机号 */}
                <div>
                  <div
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors ${errors.phone ? "bg-red-50" : "bg-gray-50"}`}
                    style={
                      errors.phone ? { border: "1px solid #F43F5E" } : undefined
                    }
                  >
                    <i
                      className="ri-phone-line"
                      style={{
                        color: errors.phone ? "#F43F5E" : "#C0C4CC",
                        fontSize: 16,
                      }}
                    />
                    <input
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                      placeholder="请输入手机号"
                      value={phoneInput}
                      onChange={(e) => {
                        setPhoneInput(e.target.value);
                        if (errors.phone)
                          setErrors((prev) => ({ ...prev, phone: undefined }));
                      }}
                      maxLength={11}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-xs text-red-500 pl-4 mt-1">
                      {errors.phone}
                    </p>
                  )}
                </div>
              </div>
              <div
                className="flex items-start gap-2 px-3 py-2.5 rounded-xl"
                style={{ background: "#EDFCF5" }}
              >
                <i
                  className="ri-shield-check-line flex-shrink-0 mt-0.5"
                  style={{ color: "#10B981", fontSize: 15 }}
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  您的信息通过国家权威认证机构验证，严格保密。
                </span>
              </div>
            </div>
            {/* 固定底部按钮区 */}
            <div className="flex gap-3 p-6 pt-3 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={handleCloseModal}
                className="flex-1 py-3.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap bg-gray-100 text-gray-500"
              >
                取消
              </button>
              <button
                onClick={handleSubmitVerify}
                disabled={submitting}
                className="flex-1 py-3.5 rounded-full text-sm font-bold text-white cursor-pointer whitespace-nowrap disabled:opacity-60"
                style={{ background: submitting ? "#CCC" : BRAND }}
              >
                {submitting ? "提交中..." : "提交认证"}
              </button>
            </div>
          </div>
        </div>
      )}

      <UserBottomNav />
    </div>
  );
}
